// Formula variable reference — the single documented list of every named
// variable an EstimateCostFormulas expression can reference, grouped by the
// context that binds it. Rendered read-only on Setup ▸ Estimate Engine ▸
// Formulas so an author can see what they can pull from without reading
// engine source. src/test/formulaVariables.test.js proves the "room" and
// "engine" groups match exactly what roomFormulaVariables() actually binds,
// so this page can't drift from the engine.
//
// Engine Variables (the TakeoffVariables table, db/65/68/69 — admin-tuned
// constants the engines read: snap tolerances, HEPA default cfm, lighting
// CU/LLF…) are ALSO referenceable in a formula, by their ALL_CAPS key
// (roomFormulaEngine.js engineVariableBindings). They come from the DB, so
// the Formulas tab appends them as a live group via buildFormulaVariableGroups()
// rather than listing them statically here.

export const ENGINE_VARIABLES_GROUP_KEY = "engine_vars";

export const FORMULA_VARIABLE_GROUPS = [
  {
    key: "room",
    title: "Room — geometry & criteria",
    where: "Per room. Bound when a formula is an Item/Assembly's Takeoff Mapping (Room Formula — summed across every room in the revision), a Room Template Standard Element's quantity, or a room element's quantity in the Room Detail Flyout.",
    vars: [
      { name: "L",              unit: "ft",  desc: "Room length (bounding box)" },
      { name: "W",              unit: "ft",  desc: "Room width (bounding box)" },
      { name: "H",              unit: "ft",  desc: "Room height" },
      { name: "floorArea",      unit: "SF",  desc: "Floor area — the real traced outline when the room has one, else L × W. Ceiling area is the same number." },
      { name: "perimeter",      unit: "LF",  desc: "Room perimeter — real outline when traced, else 2 × (L + W). Wall area is perimeter × H." },
      { name: "ach",            unit: "/hr", desc: "Air changes per hour (the room's ACH)" },
      { name: "doorCount",      unit: "ea",  desc: "Architectural criterion: doors (legacy count default)" },
      { name: "windowCount",    unit: "ea",  desc: "Architectural criterion: vision panels / windows" },
      { name: "passThroughs",   unit: "ea",  desc: "Architectural criterion: pass-throughs" },
      { name: "occupancyCount", unit: "ea",  desc: "Mechanical criterion: design occupancy" },
      { name: "exteriorWallFt", unit: "LF",  desc: "Mechanical criterion: exterior wall length" },
      { name: "outletCount",    unit: "ea",  desc: "Receptacles — the room's receptacle element(s), else the legacy criterion" },
      { name: "cdaDrops",       unit: "ea",  desc: "Compressed dry air drops — the room's CDA drop element(s), else the legacy criterion" },
      { name: "n2Drops",        unit: "ea",  desc: "Nitrogen drops — the room's N₂ drop element(s), else the legacy criterion" },
      { name: "vacDrops",       unit: "ea",  desc: "Vacuum drops — the room's vacuum drop element(s), else the legacy criterion" },
      { name: "pwDrops",        unit: "ea",  desc: "Purified water drops — the room's PW/DI drop element(s), else the legacy criterion" },
    ],
  },
  {
    key: "engine",
    title: "Room — engineering engine outputs",
    where: "Per room, same contexts as above. Calculated by the HEPA & Lighting engine with the room's overrides already applied — consume these, never rebuild them (e.g. use hepaCount, not floorArea*H*ach/60/500).",
    vars: [
      { name: "roomVolume",             unit: "CF",  desc: "floorArea × H" },
      { name: "supplyCfm",              unit: "CFM", desc: "Required supply airflow: roomVolume × ach / 60" },
      { name: "hepaCount",              unit: "ea",  desc: "Effective HEPA count (override ?? calculated); 0 until the room has a HEPA element" },
      { name: "cfmPerHepa",             unit: "CFM", desc: "Actual airflow per filter at the effective count; 0 until the room has a HEPA element the engine counts (Generated system = HEPA) — never divide by it. Use HEPA_DEFAULT_DESIGN_CFM for the design airflow" },
      { name: "hepaCoveragePercent",    unit: "%",   desc: "Actual ceiling coverage at the effective count" },
      { name: "hepaFaceVelocity",       unit: "fpm", desc: "Face velocity at the effective count" },
      { name: "lightingFixtureCount",   unit: "ea",  desc: "Effective light-fixture count (override ?? calculated ?? legacy fallback)" },
      { name: "lightingRequiredLumens", unit: "lm",  desc: "floorArea × target footcandles" },
      { name: "lightingWatts",          unit: "W",   desc: "Fixture count × watts per fixture" },
      { name: "lightingVA",             unit: "VA",  desc: "Fixture count × VA per fixture (electrical load)" },
    ],
  },
  {
    key: "circuit",
    title: "Electrical circuit generation",
    where: "Bound only when the formula is picked as a project's homerun / branch-route / feeder-route formula on the Electrical Estimate (circuit generation settings).",
    vars: [
      { name: "planDistanceFt",      unit: "ft",  desc: "Plan distance from the room centroid to its panel (homerun formula)" },
      { name: "routingFactor",       unit: "×",   desc: "Circuit settings: routing factor" },
      { name: "verticalAllowanceFt", unit: "ft",  desc: "Circuit settings: vertical allowance" },
      { name: "homerunLF",           unit: "LF",  desc: "Resolved homerun length (branch / feeder formulas)" },
      { name: "deviceSpacingFt",     unit: "ft",  desc: "Circuit settings: device-to-device spacing (branch formula)" },
      { name: "deviceCount",         unit: "ea",  desc: "Devices on the circuit (branch formula)" },
      { name: "wasteFactorPct",      unit: "frac", desc: "Circuit settings: waste factor as a fraction (0.10 = 10%)" },
    ],
  },
  {
    key: "assembly",
    title: "Assembly quantity",
    where: "Bound only when the formula is an Assembly's Quantity Formula on a priced line item.",
    vars: [
      { name: "routeLF", unit: "LF", desc: "The line's route length" },
      { name: "qty",     unit: "ea", desc: "The line's quantity" },
    ],
  },
];

export const ROOM_FORMULA_VARIABLE_NAMES = FORMULA_VARIABLE_GROUPS
  .filter(g => g.key === "room" || g.key === "engine")
  .flatMap(g => g.vars.map(v => v.name));

const CATEGORY_LABELS = {
  room_boundary: "Room boundary / drawing", hvac_duct_sizing: "Duct sizing", hepa_sizing: "HEPA sizing",
  lighting_design: "Lighting design", auto_layout: "Generated symbol layout", wall_learning: "Wall learning",
};

// Static groups + one live "Engine Variables" group built from
// sp_GetTakeoffVariables rows ({ VariableKey, Value, Label, Description,
// Category }). Every row is referenceable by key in any room-bound formula.
export function buildFormulaVariableGroups(engineVariableRows = []) {
  const vars = engineVariableRows
    .filter(r => r?.VariableKey)
    .map(r => ({
      name: r.VariableKey, unit: r.Value == null ? "" : String(+r.Value),
      desc: `${r.Label || r.VariableKey}${r.Description ? ` — ${r.Description}` : ""}`,
      category: CATEGORY_LABELS[r.Category] || r.Category || "",
    }));
  if (!vars.length) return FORMULA_VARIABLE_GROUPS;
  return [
    ...FORMULA_VARIABLE_GROUPS,
    {
      key: ENGINE_VARIABLES_GROUP_KEY,
      title: "Engine Variables (by key)",
      where: "Bound in every room-context formula (Takeoff Mapping, Room Template element, room element) at the admin-tuned value on the Engine Variables tab; the column shows the current value. Use one instead of a literal so a tuning change flows through every formula.",
      unitHeader: "Value",
      vars,
    },
  ];
}
